/* eslint-disable camelcase */
import { invisiblyApi } from '../api/invisibly-api';
import { InvisiblyApiResponse } from "../../services/api/api-response.types";
import {
  CreateInsertionOrderRequest,
  InsertionOrder,
  InsertionOrderAutocompleteSuggestions,
  InvoiceGroup,
  InvoiceGroupDetails,
  Terms,
  SKU,
  Invoice,
  InvoiceLineItem,
  CompanyMapping,
  CreateCompanyMappingRequest,
  InvoiceGroupPreviewItem,
  DeleteInvoiceValidationFileResult,
  ValidationFile,
  InvoiceLineItemValidation,
  InvoiceLineItemValidationsPaginated,
  InvoiceLineItemValidationsPaginatedItems,
  normalizeInvoiceLineItemValidationsItem,
  BillingNeededActionItem,
  BillingNeededActionsPaginated,
  BillingNeededActionsPaginatedDrf,
  BillingDashboardApiItem,
  BillingOutstandingInvoiceApiItem,
  EMPTY_BILLING_DASHBOARD_API_ITEM,
  SKUPatchPayload,
  BillingJobStatusResponse,
} from './billing.types';
import { APITagType, TagInvalidator } from '../api/invisibly-api.types';
import { Company } from 'src/pages/billing/types/billing.types';
import type { SkuSuggestion, SkuSuggestionContext } from 'src/components/sku-helper/sku-helper.types';

const invoiceLineItemsTagId = (invoiceId: string) => `LINE_ITEMS:${invoiceId}`;

function invoiceGroupInvalidationTags(
  invoiceGroupId?: string | null,
  invoiceId?: string | null,
): TagInvalidator[] {
  const tags: TagInvalidator[] = invoiceGroupId
    ? [{ type: APITagType.InvoiceGroup, id: invoiceGroupId }]
    : [APITagType.InvoiceGroup];
  if (invoiceId) {
    tags.push({ type: APITagType.InvoiceGroup, id: invoiceLineItemsTagId(invoiceId) });
  }
  return tags;
}

function lineItemMutationInvalidationTags(
  result?: Pick<InvoiceLineItem, 'invoice_group' | 'invoice'> | null,
  arg?: { invoice_group?: string; invoice?: string | null },
): TagInvalidator[] {
  if (result?.invoice_group) {
    return invoiceGroupInvalidationTags(result.invoice_group, result.invoice ?? undefined);
  }
  if (arg?.invoice_group || arg?.invoice) {
    return invoiceGroupInvalidationTags(arg.invoice_group, arg.invoice ?? undefined);
  }
  return [APITagType.InvoiceGroup];
}

function normalizeBillingNeededActionsResponse(
  item:
  | BillingNeededActionItem[]
  | BillingNeededActionsPaginated
  | BillingNeededActionsPaginatedDrf
  | null
  | undefined
): BillingNeededActionItem[] {
  if (!item) return [];
  if (Array.isArray(item)) return item;
  if (typeof item === 'object' && Array.isArray((item as BillingNeededActionsPaginated).items)) {
    return (item as BillingNeededActionsPaginated).items;
  }
  if (typeof item === 'object' && Array.isArray((item as BillingNeededActionsPaginatedDrf).results)) {
    return (item as BillingNeededActionsPaginatedDrf).results;
  }
  return [];
}

const billingEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<Company[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/companies',
      }),
      transformResponse(response: InvisiblyApiResponse<Company[]>) {
        return response.item;
      },
      providesTags: [APITagType.Company],
    }),
    getTerms: builder.query<Terms[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/terms',
      }),
      transformResponse(response: InvisiblyApiResponse<Terms[]>) {
        return response.item;
      },
    }),
    getSKUs: builder.query<SKU[], { includeArchived?: boolean } | void>({
      query: (arg) => ({
        method: 'get',
        url: 'b2b/billing/skus',
        ...(arg?.includeArchived ? { params: { include_archived: true } } : {}),
      }),
      transformResponse(response: InvisiblyApiResponse<SKU[]>) {
        return response.item;
      },
      providesTags: [{ type: APITagType.BillingSku, id: 'LIST' }],
    }),
    getSKU: builder.query<SKU, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/skus/${id}`,
      }),
      transformResponse(response: InvisiblyApiResponse<SKU>) {
        return response.item;
      },
      providesTags: (_result, _error, id) => [{ type: APITagType.BillingSku, id }],
    }),
    patchSKU: builder.mutation<SKU, { id: string; data: SKUPatchPayload }>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/skus/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<SKU>) {
        return response.item;
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: APITagType.BillingSku, id: 'LIST' },
        { type: APITagType.BillingSku, id },
      ],
    }),
    syncSKUs: builder.mutation<string, void>({
      query: () => ({
        method: 'post',
        url: 'b2b/billing/skus/sync',
      }),
      transformResponse(response: InvisiblyApiResponse<string>) {
        return response.item;
      },
      invalidatesTags: [{ type: APITagType.BillingSku, id: 'LIST' }],
    }),
    getSkuSuggestions: builder.mutation<SkuSuggestion[], SkuSuggestionContext>({
      query: (context) => ({
        method: 'post',
        url: 'b2b/billing/sku-suggestions',
        data: context,
      }),
      transformResponse(response: InvisiblyApiResponse<SkuSuggestion[]>) {
        return response.item;
      },
    }),
    getInsertionOrders: builder.query<InsertionOrder[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/insertion_orders',
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder[]>) {
        return response.item;
      },
      providesTags: [APITagType.InsertionOrder],
    }),
    getInvoiceGroups: builder.query<InvoiceGroup[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/invoice_groups',
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroup[]>) {
        return response.item;
      },
      providesTags: [APITagType.InvoiceGroup],
    }),
    getInvoiceGroupDetails: builder.query<InvoiceGroupDetails, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/invoice_groups/${id}`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroupDetails>) {
        return response.item;
      },
      providesTags: (_result, _error, id) => [{ type: APITagType.InvoiceGroup, id }],
    }),
    getInsertionOrderFields: builder.query<InsertionOrderAutocompleteSuggestions, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/insertion_orders/fields',
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrderAutocompleteSuggestions>) {
        return response.item;
      },
    }),
    createInsertionOrder: builder.mutation<InsertionOrder, CreateInsertionOrderRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/insertion_orders',
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    updateInsertionOrder: builder.mutation<InsertionOrder, { id: string; data: CreateInsertionOrderRequest }>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/insertion_orders/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    getInsertionOrderPreview: builder.query<InsertionOrder, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/insertion_orders/${id}/preview`,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
    }),
    sendInsertionOrder: builder.mutation<InsertionOrder, string>({
      query: (id) => ({
        method: 'post',
        url: `b2b/billing/insertion_orders/${id}/send`,
      }),
      transformResponse(response: InvisiblyApiResponse<InsertionOrder>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    deleteInsertionOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `b2b/billing/insertion_orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    cloneInsertionOrder: builder.mutation<InsertionOrder, string>({
      query: (id) => ({
        url: `b2b/billing/insertion_orders/${id}/clone`,
        method: 'POST',
      }),
      invalidatesTags: [APITagType.InsertionOrder],
    }),
    getBillingJobStatus: builder.query<BillingJobStatusResponse, string>({
      query: (billingMonthId) => ({
        method: 'get',
        url: `b2b/billing/jobs/${billingMonthId}/status`,
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<BillingJobStatusResponse>) {
        return response.item;
      },
    }),
    createInvoiceGroup: builder.mutation<InvoiceGroup, { month: number; year: number; isInitial?: boolean }>({
      query: ({ month, year, isInitial = false }) => ({
        method: 'post',
        url: `b2b/billing/invoice_groups?isInitial=${isInitial ? 'true' : 'false'}`,
        data: { month, year, isInitial },
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroup>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    uploadInvoiceValidationFile: builder.mutation<InvoiceGroupDetails, { file: File; id: string; source: string }>({
      query: ({ file, id, source }) => {
        const fd = new FormData();
        fd.append('file', file, file.name);
        fd.append('source', source);
        return { url: `b2b/billing/invoice_groups/${id}/validate/upload`, method: 'POST', data: fd };
      },
      transformResponse(response: InvisiblyApiResponse<InvoiceGroupDetails>) {
        return response.item;
      },
      async onQueryStarted({ file, id, source }, { dispatch, queryFulfilled }) {
        const optimisticFile: ValidationFile = {
          filename: file.name,
          source,
          rowcount: 0,
          uploaded_at: new Date().toISOString(),
        };
        const patch = dispatch(
          invisiblyApi.util.updateQueryData('getInvoiceGroupDetails', id, (draft) => {
            if (!draft.validations) {
              draft.validations = { files: [] };
            }
            draft.validations.files ??= [];
            const alreadyListed = draft.validations.files.some(
              (existing) => existing.filename === file.name && existing.source === source,
            );
            if (!alreadyListed) {
              draft.validations.files.push(optimisticFile);
            }
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            invisiblyApi.util.updateQueryData('getInvoiceGroupDetails', id, () => data),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    runInvoiceGroupValidation: builder.mutation<InvoiceGroupDetails, string>({
      query: (id) => ({
        method: 'post',
        url: `b2b/billing/invoice_groups/${id}/validate`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroupDetails>) {
        return response.item;
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            invisiblyApi.util.updateQueryData('getInvoiceGroupDetails', id, () => data),
          );
        } catch {
          // No optimistic draft to revert.
        }
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    deleteInvoiceValidationFile: builder.mutation<DeleteInvoiceValidationFileResult, { id: string; source: string; filename: string }>({
      query: ({ id, source, filename }) => ({
        method: 'delete',
        url: `b2b/billing/invoice_groups/${id}/validate/upload/remove/`,
        data: { source, filename },
      }),
      transformResponse(response: InvisiblyApiResponse<DeleteInvoiceValidationFileResult>) {
        return response.item;
      },
      async onQueryStarted({ id, source, filename }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          invisiblyApi.util.updateQueryData('getInvoiceGroupDetails', id, (draft) => {
            if (!draft.validations?.files) {
              return;
            }
            draft.validations.files = draft.validations.files.filter(
              (file) => !(file.filename === filename && file.source === source),
            );
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    getInvoiceLineItemValidations: builder.query<
    InvoiceLineItemValidation[],
    { line_item?: string; invoice_group?: string }
    >({
      query: ({ line_item, invoice_group }) => {
        const params: Record<string, string> = {};
        if (line_item != null && line_item !== '') {
          params.line_item = line_item;
        }
        if (invoice_group != null && invoice_group !== '') {
          params.invoice_group = invoice_group;
        }
        return {
          method: 'get',
          url: 'b2b/billing/invoice_line_item_validations',
          params,
        };
      },
      transformResponse(
        response: InvisiblyApiResponse<
        | InvoiceLineItemValidation[]
        | InvoiceLineItemValidationsPaginated
        | InvoiceLineItemValidationsPaginatedItems
        >
      ) {
        return normalizeInvoiceLineItemValidationsItem(response.item);
      },
      providesTags: (_result, _err, arg) => [
        { type: APITagType.InvoiceLineItemValidation, id: 'LIST' },
        ...(arg.line_item
          ? [{ type: APITagType.InvoiceLineItemValidation, id: `LINE:${arg.line_item}` } as const]
          : []),
        ...(arg.invoice_group
          ? [{ type: APITagType.InvoiceLineItemValidation, id: `GROUP:${arg.invoice_group}` } as const]
          : []),
      ],
    }),
    patchInvoiceLineItemValidation: builder.mutation<
    InvoiceLineItemValidation,
    { id: string; data: Record<string, unknown>; invoiceGroupId?: string }
    >({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/invoice_line_item_validations/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItemValidation>) {
        return response.item;
      },
      invalidatesTags: (result, _err, arg) => [
        { type: APITagType.InvoiceLineItemValidation, id: 'LIST' },
        ...(result?.line_item
          ? [{ type: APITagType.InvoiceLineItemValidation, id: `LINE:${result.line_item}` } as const]
          : []),
        ...(arg.invoiceGroupId != null && arg.invoiceGroupId !== ''
          ? [{ type: APITagType.InvoiceGroup, id: arg.invoiceGroupId } as const]
          : [APITagType.InvoiceGroup]),
      ],
    }),
    /** Valid platform source identifiers for validation CSV uploads (POST .../validate/upload/). */
    getCsvValidateSources: builder.query<string[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/invoice_groups/csv-validate-sources',
      }),
      transformResponse(response: InvisiblyApiResponse<string[]>) {
        return response.item ?? [];
      },
    }),
    getInvoice: builder.query<Invoice, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/invoices/${id}`,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
    }),
    getInvoiceLineItems: builder.query<InvoiceLineItem[], string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/invoices/${id}/line-items`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem[]>) {
        return response.item ?? [];
      },
      providesTags: (_result, _error, invoiceId) => [
        { type: APITagType.InvoiceGroup, id: invoiceLineItemsTagId(invoiceId) },
      ],
    }),
    /** Retrieve a single line item (for pipeline review deep links when backend supports GET). */
    getInvoiceLineItem: builder.query<InvoiceLineItem, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/invoice_line_items/${id}`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem>) {
        return response.item;
      },
    }),
    updateInvoice: builder.mutation<Invoice, { id: string; data: Partial<Invoice> }>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/invoices/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: (result) => invoiceGroupInvalidationTags(result?.invoice_group, result?.id),
    }),
    resetInvoice: builder.mutation<Invoice, { id: string; }>({
      query: ({ id }) => ({
        method: 'post',
        url: `b2b/billing/invoices/${id}/reset`,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: (result) => invoiceGroupInvalidationTags(result?.invoice_group, result?.id),
    }),
    updateInvoiceStatus: builder.mutation<Invoice, { id: string; data: {invoice_status: string} }>({
      query: ({ id, data }) => ({
        method: 'post',
        url: `b2b/billing/invoices/${id}/status`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: (result) => invoiceGroupInvalidationTags(result?.invoice_group, result?.id),
    }),
    syncInvoice: builder.mutation<string, { id: string }>({
      query: ({ id }) => ({
        method: 'post',
        url: `b2b/billing/invoices/${id}/sync`,
      }),
      transformResponse(response: InvisiblyApiResponse<string>) {
        return response.item;
      },
      invalidatesTags: (_result, _error, { id }) => [
        APITagType.InvoiceGroup,
        { type: APITagType.InvoiceGroup, id: invoiceLineItemsTagId(id) },
      ],
    }),
    downloadInvoice: builder.mutation<string | null, string>({
      query: (id) => ({
        method: 'post',
        url: `b2b/billing/invoices/${id}/download`,
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<string>) {
        return response.item;
      },
    }),
    updateInvoiceLineItem: builder.mutation<InvoiceLineItem, { id: string; data: Partial<InvoiceLineItem> }>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `b2b/billing/invoice_line_items/${id}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem>) {
        return response.item;
      },
      invalidatesTags: (result, _error, arg) =>
        lineItemMutationInvalidationTags(result, {
          invoice_group: typeof arg.data.invoice_group === 'string' ? arg.data.invoice_group : undefined,
          invoice: typeof arg.data.invoice === 'string' ? arg.data.invoice : undefined,
        }),
    }),
    destroyInvoiceLineItem: builder.mutation<InvoiceLineItem | string, { id: string; invoice_group?: string; invoice?: string | null }>({
      query: ({ id }) => ({
        method: 'delete',
        url: `b2b/billing/invoice_line_items/${id}`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem | string>) {
        return response.item;
      },
      invalidatesTags: (result, _error, arg) =>
        lineItemMutationInvalidationTags(
          typeof result === 'object' && result != null && 'invoice_group' in result ? result : null,
          arg,
        ),
    }),
    resetInvoiceLineItem: builder.mutation<InvoiceLineItem | string, { id: string; invoice_group?: string; invoice?: string | null }>({
      query: ({ id }) => ({
        method: 'post',
        url: `b2b/billing/invoice_line_items/${id}/reset`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem | string>) {
        return response.item;
      },
      invalidatesTags: (result, _error, arg) =>
        lineItemMutationInvalidationTags(
          typeof result === 'object' && result != null && 'invoice_group' in result ? result : null,
          arg,
        ),
    }),
    createInvoice: builder.mutation<Invoice, { invoice_group: string; company: string; attention_needed?: boolean }>({
      query: ({ invoice_group, company, attention_needed }) => ({
        method: 'post',
        url: 'b2b/billing/invoices',
        data: { invoice_group, company, attention_needed },
      }),
      transformResponse(response: InvisiblyApiResponse<Invoice>) {
        return response.item;
      },
      invalidatesTags: [APITagType.InvoiceGroup],
    }),
    createInvoiceLineItem: builder.mutation<InvoiceLineItem, { name: string; invoice_group: string; invoice: string; attention_needed?: boolean }>({
      query: ({ name, invoice_group, invoice, attention_needed }) => ({
        method: 'post',
        url: 'b2b/billing/invoice_line_items',
        data: { name, invoice_group, invoice, attention_needed, qty: 1 },
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceLineItem>) {
        return response.item;
      },
      invalidatesTags: (result, _error, arg) =>
        invoiceGroupInvalidationTags(
          result?.invoice_group ?? arg.invoice_group,
          result?.invoice ?? arg.invoice,
        ),
    }),
    getCompanyMappings: builder.query<CompanyMapping[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/company_mapping',
      }),
      transformResponse(response: InvisiblyApiResponse<CompanyMapping[]>) {
        return response.item;
      },
      providesTags: [APITagType.Company],
    }),
    createCompanyMapping: builder.mutation<CompanyMapping, CreateCompanyMappingRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/company_mapping',
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<CompanyMapping>) {
        return response.item;
      },
      invalidatesTags: [APITagType.Company],
    }),
    deleteCompanyMapping: builder.mutation<void, string>({
      query: (id) => ({
        method: 'delete',
        url: `b2b/billing/company_mapping/${id}`,
      }),
      invalidatesTags: [APITagType.Company],
    }),
    getInvoiceGroupPreview: builder.query<InvoiceGroupPreviewItem[], string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/billing/invoice_groups/${id}/preview`,
      }),
      transformResponse(response: InvisiblyApiResponse<InvoiceGroupPreviewItem[]>) {
        return response.item;
      },
    }),
    createMapping: builder.mutation<void, CreateCompanyMappingRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/company_mapping',
        data,
      }),
    }),
    createCampaignMapping: builder.mutation<void, CreateCompanyMappingRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/billing/company_mapping',
        data,
      }),
    }),
    getBillingNeededActions: builder.query<BillingNeededActionItem[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/dashboard/needed_actions',
        params: { page: 1, page_size: 50 },
      }),
      transformResponse(
        response: InvisiblyApiResponse<
        BillingNeededActionItem[] | BillingNeededActionsPaginated | BillingNeededActionsPaginatedDrf
        >
      ) {
        const rows = normalizeBillingNeededActionsResponse(response.item);
        return rows.filter((row) => row.attention_needed);
      },
      providesTags: [APITagType.BillingNeededActions],
    }),
    getBillingDashboard: builder.query<BillingDashboardApiItem, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/dashboard',
      }),
      transformResponse(response: InvisiblyApiResponse<BillingDashboardApiItem | null>) {
        if (response.item == null) {
          return EMPTY_BILLING_DASHBOARD_API_ITEM;
        }
        return response.item;
      },
      providesTags: [APITagType.BillingDashboard],
    }),
    getBillingOutstandingInvoices: builder.query<BillingOutstandingInvoiceApiItem[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/billing/dashboard/outstanding-invoices',
      }),
      transformResponse(response: InvisiblyApiResponse<BillingOutstandingInvoiceApiItem[] | null>) {
        return Array.isArray(response.item) ? response.item : [];
      },
      providesTags: [APITagType.BillingDashboard],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetCompaniesQuery,
  useGetTermsQuery,
  useGetSKUsQuery,
  useGetSKUQuery,
  useLazyGetSKUQuery,
  usePatchSKUMutation,
  useSyncSKUsMutation,
  useGetSkuSuggestionsMutation,
  useGetInsertionOrdersQuery,
  useGetInvoiceGroupsQuery,
  useGetInvoiceGroupDetailsQuery,
  useGetInsertionOrderFieldsQuery,
  useCreateInsertionOrderMutation,
  useUploadInvoiceValidationFileMutation,
  useRunInvoiceGroupValidationMutation,
  useDeleteInvoiceValidationFileMutation,
  useGetInvoiceLineItemValidationsQuery,
  usePatchInvoiceLineItemValidationMutation,
  useGetCsvValidateSourcesQuery,
  useUpdateInsertionOrderMutation,
  useGetInsertionOrderPreviewQuery,
  useLazyGetInsertionOrderPreviewQuery,
  useSendInsertionOrderMutation,
  useDeleteInsertionOrderMutation,
  useCloneInsertionOrderMutation,
  useGetBillingJobStatusQuery,
  useLazyGetBillingJobStatusQuery,
  useCreateInvoiceGroupMutation,
  useUpdateInvoiceMutation,
  useUpdateInvoiceStatusMutation,
  useSyncInvoiceMutation,
  useResetInvoiceMutation,
  useUpdateInvoiceLineItemMutation,
  useDestroyInvoiceLineItemMutation,
  useCreateInvoiceMutation,
  useGetInvoiceQuery,
  useGetInvoiceLineItemsQuery,
  useLazyGetInvoiceLineItemsQuery,
  useLazyGetInvoiceQuery,
  useGetInvoiceLineItemQuery,
  useLazyGetInvoiceLineItemQuery,
  useCreateInvoiceLineItemMutation,
  useResetInvoiceLineItemMutation,
  useGetCompanyMappingsQuery,
  useCreateCompanyMappingMutation,
  useDeleteCompanyMappingMutation,
  useGetInvoiceGroupPreviewQuery,
  useCreateMappingMutation,
  useCreateCampaignMappingMutation,
  useDownloadInvoiceMutation,
  useGetBillingNeededActionsQuery,
  useGetBillingDashboardQuery,
  useGetBillingOutstandingInvoicesQuery,
} = billingEndpoints;